import { Link } from 'react-router-dom';

const values = [
  { icon: '🧡', title: 'Circular by design',  body: 'Every item sold on Souqli is pre-loved. We exist because the best products deserve a second life, and a third.' },
  { icon: '🔒', title: 'Trust at every step', body: 'Sellers are identity-verified. Payments are secured by Stripe. Disputes are handled by humans, not chatbots.' },
  { icon: '🎯', title: 'Curated, not cluttered', body: 'We back quality over volume. Our seller tier system rewards honest, detailed listings and fast shipping.' },
  { icon: '🌍', title: 'Community first',     body: 'Souqli is built around the sellers who make it great. Better tools, fairer fees, and real support.' },
];


export default function About() {
  return (
    <div className="section-dark bg-ink text-cream min-h-screen">

      {/* HERO */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-10 pointer-events-none" />
        <div className="relative max-w-[800px] mx-auto px-6 md:px-10 text-center">
          <span className="pill bg-gold text-ink text-[10px] mb-4 inline-block">ABOUT SOUQLI</span>
          <h1 className="display text-4xl md:text-5xl text-cream mb-5 leading-tight">
            Pre-loved fashion,<br />properly done.
          </h1>
          <p className="text-cream/60 text-base md:text-lg max-w-[54ch] mx-auto leading-relaxed">
            Souqli is a curated recommerce marketplace built for people who take fashion seriously.
            Better sellers, better buyers, and the tools to make second-hand feel like it should.
          </p>
        </div>
      </section>

      {/* OUR VALUES */}
      <section className="border-t border-cream/10 py-16">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <h2 className="display text-2xl text-cream text-center mb-2">What we stand for</h2>
          <p className="text-cream/45 text-sm text-center mb-10">Four things we will not compromise on.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            {values.map(v => (
              <div key={v.title} className="bg-cream/5 border border-cream/10 hover:border-gold/30 rounded-luxe p-6 transition-all">
                <span className="text-2xl mb-3 block">{v.icon}</span>
                <h3 className="display text-base text-cream mb-2">{v.title}</h3>
                <p className="text-cream/55 text-sm leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="border-t border-cream/10 py-16">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <h2 className="display text-2xl text-cream mb-6">The story</h2>
          <div className="space-y-4 text-cream/65 text-sm leading-relaxed">
            <p>
              Souqli started with a frustration most resellers know well: platforms that let lowball buyers,
              slow payouts, and poor support grind down the experience of selling well-cared-for pieces.
            </p>
            <p>
              We built Souqli to fix that. A marketplace where the best sellers get the visibility they
              deserve, buyers are held to a higher standard, and every payment is handled securely by Stripe.
            </p>
            <p>
              From curated seller tiers to AI listing tools, and from instant payouts to disputes resolved by
              real people, every decision we make begins with a single question: does this make things better for the seller?
            </p>
          </div>
        </div>
      </section>

      {/* SUSTAINABILITY COMMITMENT */}
      <section className="border-t border-cream/10 py-16">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <h2 className="display text-2xl text-cream mb-3">Sustainability is not a feature</h2>
          <p className="text-cream/60 text-sm leading-relaxed mb-6">
            We do not add a green banner and call it a day. Recommerce is the model. Every sale on Souqli
            replaces a new purchase somewhere. That is not marketing copy; it is arithmetic.
          </p>
          <div className="grid grid-cols-3 gap-4 text-center">
            {[
              { icon: '♻️', label: 'Circular model',     detail: 'No new stock, ever' },
              { icon: '📦', label: 'Eco packaging',       detail: 'Locker delivery default' },
              { icon: '🌱', label: 'Impact tracking',     detail: 'CO₂ shown per sale'     },
            ].map(i => (
              <div key={i.label} className="bg-cream/5 border border-cream/10 rounded-luxe p-4">
                <div className="text-2xl mb-2">{i.icon}</div>
                <p className="text-sm font-medium text-cream">{i.label}</p>
                <p className="text-xs text-cream/45 mt-1">{i.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-cream/10 py-20">
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <h2 className="display text-3xl text-cream mb-3">Join the marketplace</h2>
          <p className="text-cream/50 text-sm mb-8">Whether you sell, buy, or simply browse, Souqli is better with you in it.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="btn btn-gold text-sm px-10 py-3.5">Create a free account &rarr;</Link>
            <Link to="/c/all" className="btn btn-outline-cream text-sm px-8 py-3.5">Browse the market</Link>
          </div>
          <p className="text-cream/25 text-xs mt-6">
            Designed &amp; built by{' '}
            <a href="mailto:info@souqli.co.uk" className="text-gold hover:underline">Synterra Technologies</a>
          </p>
        </div>
      </section>

    </div>
  );
}
